import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import WHOStepper from "../../../controls/WHOStepper";
import { StepperModel } from "../../../../models/StepperModel";
import { DQASteps, Preference, StrategiesEnum } from "../../../../models/Enums";
import classes from "../../data-collection/data-quality-assessment/dqa.module.scss";
import IndicatorsSelection from "../../data-analysis/data-quality-assessment/desk-level-delivery/IndicatorsSelection"
import { VariableModel } from "../../../../models/VariableModel";
import { dqaService } from "../../../../services/dqaService";
import SummaryResult from "./service-level-delivery/SummaryResult";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Step, StepLabel, Stepper, Typography } from "@mui/material";
import EliminationSummaryResult from "./EliminationSummaryResult"
import { UtilityHelper } from "../../../../utils/UtilityHelper";

type DQAContainerProps = {
    strategyId: string;
    assessmentId: string;
};

/** Renders the DQA Container for data analysis and all components underneath */
const DQAContainer = (props: DQAContainerProps) => {
    const { t } = useTranslation();
    document.title = t("app.DQATitle");
    const { strategyId, assessmentId } = props;
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [priorityVariables, setPriorityVariables] = useState<Array<VariableModel>>([]);
    const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
    const [currentExportSteps, setCurrentExportSteps] = useState<string[]>([]);

    const exportSteps = [
        `${t("Common.Exporting")}...`,
        t("Common.ExportStepFetchingData"),
        t("Common.ExportStepGeneratingFile"),
    ];

    const handleProgress = (step: string) => {
        const idx = exportSteps.indexOf(step);
        setActiveStepIndex(idx >= 0 ? idx : 0);
    };
    const handleAllComplete = () => setActiveStepIndex(exportSteps.length);
    const resetExportProgress = () => setActiveStepIndex(-1);

    const currentlanguage = UtilityHelper.getCookieValue("i18next");

    useEffect(() => {
        getDQACoreVariables();
    }, [strategyId, currentlanguage]);

    // get the core variables for DQA
    const getDQACoreVariables = () => {
        if (!strategyId) return;

        dqaService
            .getDQACoreVariables(strategyId, false)
            .then((response: Array<VariableModel>) => {
                setPriorityVariables(response.filter(variable => variable.priority === Preference.Priority));
            });
    };

    // Triggers when step is changed
    const onStepChange = (currentStep: number) => {
        setCurrentStep(currentStep);
    };

    // Triggers whenever user clicks on export button
    const onGenerateReport = () => {
        const downloadedTemplateName = t("app.DQADeskLevelToolReportFileName");
        setCurrentExportSteps(exportSteps);
        setActiveStepIndex(0);
        dqaService
            .downloadReport(assessmentId, handleProgress, handleAllComplete)
            .then((blob: Blob) => {
                resetExportProgress();
                UtilityHelper.download(blob, downloadedTemplateName);
            })
            .catch(() => {
                resetExportProgress();
            });
    }

    return (
        <section className="page-full-section">
            {(strategyId !== StrategiesEnum.Elimination.toLowerCase()) ?
                <>
                    {(strategyId !== StrategiesEnum.Both.toLowerCase()) ?
                        <WHOStepper
                            steps={[
                                new StepperModel(
                                    DQASteps.DeskLevelDelivery,
                                    `${t("Assessment.DataCollection.DataQualityAssessment.DeskLevelDelivery")}`
                                ),
                                new StepperModel(
                                    DQASteps.ServiceLevelDelivery,
                                    `${t("Assessment.DataCollection.DataQualityAssessment.ServiceLevelDelivery")}`
                                ),
                            ]}
                            className={classNames(classes.stepWrapper)}
                            activeStep={currentStep}
                            enableStepClick
                            alternativeLabel={false}
                            nonLinear={true}
                            onStepChange={onStepChange}
                        >
                            <>
                                {currentStep === DQASteps.DeskLevelDelivery &&
                                    <>
                                        <div className="button-action-wrapper">
                                            <div className="button-action-section d-flex align-items-center justify-content-end px-4">
                                                {
                                                    <Button size="small"
                                                        className="btn app-btn-secondary"
                                                        onClick={onGenerateReport}
                                                    >
                                                        {t("Common.Export")}
                                                    </Button>
                                                }
                                            </div>
                                        </div>
                                        <div
                                            className={classNames(
                                                "d-flex",
                                                "align-items-stretch",
                                                "mt-4",
                                                classes.categoryWrapper
                                            )}
                                        >
                                            <IndicatorsSelection assessmentId={assessmentId} />
                                        </div>
                                    </>
                                }

                                {currentStep === DQASteps.ServiceLevelDelivery &&
                                    <>
                                        <div
                                            className={classNames(
                                                "align-items-stretch",
                                                "mt-4",
                                                classes.categoryWrapper
                                            )}
                                        >
                                            <SummaryResult
                                                strategyId={strategyId}
                                                assessmentId={assessmentId}
                                            />
                                        </div>
                                    </>
                                }
                            </>
                        </WHOStepper>
                        :
                        <WHOStepper
                            steps={[
                                new StepperModel(
                                    DQASteps.DeskLevelDelivery,
                                    `${t("Assessment.DataCollection.DataQualityAssessment.DeskLevelDeliveryBurden")}`
                                ),
                                new StepperModel(
                                    DQASteps.ServiceLevelDelivery,
                                    `${t("Assessment.DataCollection.DataQualityAssessment.ServiceLevelDeliveryBurden")}`
                                ),
                                new StepperModel(
                                    DQASteps.EliminationDQA,
                                    `${t("Assessment.DataCollection.DataQualityAssessment.DQAElimination")}`
                                ),
                            ]}
                            className={classNames(classes.stepWrapper)}
                            activeStep={currentStep}
                            enableStepClick
                            alternativeLabel={false}
                            nonLinear={true}
                            onStepChange={onStepChange}
                        >
                            <>
                                {currentStep === DQASteps.DeskLevelDelivery &&
                                    <>
                                        <div className="button-action-wrapper">
                                            <div className="button-action-section d-flex align-items-center justify-content-end px-4">
                                                {
                                                    <Button size="small"
                                                        className="btn app-btn-secondary"
                                                        onClick={onGenerateReport}
                                                    >
                                                        {t("Common.Export")}
                                                    </Button>
                                                }
                                            </div>
                                        </div>
                                        <div
                                            className={classNames(
                                                "d-flex",
                                                "align-items-stretch",
                                                "mt-4",
                                                classes.categoryWrapper
                                            )}
                                        >
                                            <IndicatorsSelection assessmentId={assessmentId} />
                                        </div>
                                    </>
                                }

                                {currentStep === DQASteps.ServiceLevelDelivery &&
                                    <>
                                        <div
                                            className={classNames(
                                                "align-items-stretch",
                                                "mt-4",
                                                classes.categoryWrapper
                                            )}
                                        >
                                            <SummaryResult
                                                strategyId={strategyId}
                                                assessmentId={assessmentId}
                                            />
                                        </div>
                                    </>
                                }

                                {currentStep === DQASteps.EliminationDQA &&
                                    <>
                                        <EliminationSummaryResult
                                            strategyId={strategyId}
                                            assessmentId={assessmentId}
                                        />
                                    </>
                                }
                            </>
                        </WHOStepper>
                    }
                </>
                :
                <EliminationSummaryResult
                    strategyId={strategyId}
                    assessmentId={assessmentId}
                />
            }

            <Dialog open={activeStepIndex >= 0} disableEscapeKeyDown>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} />
                        <Typography>{currentExportSteps[0] ?? `${t("Common.Exporting")}...`}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ minWidth: 480 }}>
                        {currentExportSteps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </DialogContent>
            </Dialog>
        </section>
    )
}

export default DQAContainer;